// ============================================================
// Wauvea WebXR — XR Controllers
// Controller models, ray pointers, and XR interaction
// ============================================================

import * as THREE from 'three';
import { XRControllerModelFactory } from 'three/addons/webxr/XRControllerModelFactory.js';

export class XRControllers {
  constructor(renderer, scene) {
    this.renderer = renderer;
    this.scene = scene;
    this.controllers = [];
    this.grips = [];
    this.onSelect = null;
    this.hoveredPanel = null;
  }

  build() {
    const factory = new XRControllerModelFactory();

    for (let i = 0; i < 2; i++) {
      // Controller
      const controller = this.renderer.xr.getController(i);
      controller.addEventListener('selectstart', (e) => this._onSelectStart(e));
      this.scene.add(controller);
      this.controllers.push(controller);

      // Controller grip model
      const grip = this.renderer.xr.getControllerGrip(i);
      grip.add(factory.createControllerModel(grip));
      this.scene.add(grip);
      this.grips.push(grip);

      // Ray pointer
      const rayGeo = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(0, 0, 0),
        new THREE.Vector3(0, 0, -5),
      ]);
      const rayMat = new THREE.LineBasicMaterial({
        color: 0x00D2FF,
        transparent: true,
        opacity: 0.5,
      });
      controller.add(new THREE.Line(rayGeo, rayMat));
    }
  }

  _onSelectStart(event) {
    this.onSelect?.(event.target);
  }

  /**
   * Raycast from controllers against clickable panels
   * @param {THREE.Mesh[]} clickables
   * @returns {{ controller: THREE.Group, panel: THREE.Mesh, intersection: THREE.Intersection } | null}
   */
  checkHover(clickables) {
    for (const controller of this.controllers) {
      const tempMatrix = new THREE.Matrix4();
      tempMatrix.identity().extractRotation(controller.matrixWorld);

      const raycaster = new THREE.Raycaster();
      raycaster.ray.origin.setFromMatrixPosition(controller.matrixWorld);
      raycaster.ray.direction.set(0, 0, -1).applyMatrix4(tempMatrix);

      const hits = raycaster.intersectObjects(clickables, false);
      if (hits.length > 0) {
        const panel = hits[0].object;
        if (this.hoveredPanel !== panel) {
          if (this.hoveredPanel) {
            this.hoveredPanel.material.emissiveIntensity =
              this.hoveredPanel.userData.baseEmissiveIntensity;
          }
          this.hoveredPanel = panel;
          panel.material.emissiveIntensity = 0.5;
        }
        return { controller, panel, intersection: hits[0] };
      }
    }

    // Nothing hit
    if (this.hoveredPanel) {
      this.hoveredPanel.material.emissiveIntensity =
        this.hoveredPanel.userData.baseEmissiveIntensity;
      this.hoveredPanel = null;
    }
    return null;
  }
}
